import { Navigate } from 'react-router-dom'
import { ShieldOff, UserX } from 'lucide-react'
import { useAuth } from '../contexts/AuthContext'
import { homePath } from '../lib/roles'

interface RoleRouteProps {
  allow: (role: string | null | undefined) => boolean
  children: React.ReactNode
  redirect?: boolean
}

export function RoleRoute({ allow, children, redirect }: RoleRouteProps) {
  const { profile, loading, profileLoading, signOut } = useAuth()

  if (loading || (profileLoading && !profile)) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-600"></div>
      </div>
    )
  }

  if (!profile) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center max-w-md">
          <UserX className="h-16 w-16 text-gray-400 mx-auto mb-4" />
          <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-2">Your account didn't load</h2>
          <p className="text-gray-500 dark:text-gray-400 mb-4">
            You're signed in, but your profile couldn't be loaded. Try again, or sign out and back in. If it keeps happening, ask an admin.
          </p>
          <div className="flex justify-center gap-3">
            <button
              onClick={() => window.location.reload()}
              className="px-4 py-2 bg-brand-600 text-white rounded-lg hover:bg-brand-800"
            >
              Try again
            </button>
            <button
              onClick={signOut}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-surface-0"
            >
              Sign out
            </button>
          </div>
        </div>
      </div>
    )
  }

  if (!allow(profile.role)) {
    if (redirect) return <Navigate to={homePath(profile.role)} replace />
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <ShieldOff className="h-16 w-16 text-gray-400 mx-auto mb-4" />
          <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-2">No access</h2>
          <p className="text-gray-500 dark:text-gray-400 mb-4">Your role can't open this page.</p>
          <a
            href={homePath(profile.role)}
            className="inline-block px-4 py-2 bg-brand-600 text-white rounded-lg hover:bg-brand-800"
          >
            Go to my home page
          </a>
        </div>
      </div>
    )
  }

  return <>{children}</>
}
